import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, CircularProgress } from '@material-ui/core';
import { Select } from '@campfire/select/lib';
import { useFormikContext } from 'formik';
import { useCampfireFetch } from '../../../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../../../global/config/useApiUrl';
import { SaveTaskValues } from '../../admin-console-volunteer-profile-model';

interface StripeProduct {
  id: string;
  name: string;
  type: 'one_time' | 'recurring';
  price: number;
  currency: string;
}

interface StripeProductsResponse {
  products: StripeProduct[];
}

export const StripeProductSelectionSection: React.FC = () => {
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const campfireFetch = useCampfireFetch<StripeProductsResponse>({ defer: true });
  const apiUrl = useApiUrl();
  const { values, setFieldValue } = useFormikContext<SaveTaskValues>();

  useEffect(() => {
    setLoading(true);
    campfireFetch
      .run({
        url: `${apiUrl}/wm/integrations/stripe/products/get`,
        method: 'get',
      })
      .then((response) => {
        setLoading(false);
        const productsData = response.data?.data?.products || [];
        setProducts(productsData);
      })
      .catch((err) => {
        setLoading(false);
        // Use the error message from the backend if available, otherwise fall back to generic message
        const errorMessage =
          err?.response?.data?.message || err?.message || 'Configure Stripe integration via admin console';
        setError(errorMessage);
        setProducts([]);
      });
  }, []);

  useEffect(() => {
    if (products.length === 0) return;
    const selectedType = products.find((product) => product.id === values.stripeProductId)?.type;
    if (selectedType && values.stripeProductType !== selectedType) {
      setFieldValue('stripeProductType', selectedType);
    }
  }, [products, values.stripeProductId, values.stripeProductType]);

  const formatProductDisplay = (product: StripeProduct) => {
    const price = product.price / 100; // Convert from cents
    const typeLabel = product.type === 'recurring' ? 'Subscription' : 'One-time';
    return `${product.name} - $${price} ${product.currency.toUpperCase()} (${typeLabel})`;
  };

  if (loading) {
    return (
      <Grid item xs={12}>
        <Box display='flex' alignItems='center' justifyContent='center' p={2}>
          <CircularProgress size={20} style={{ marginRight: 8 }} />
          <Typography variant='body2' color='textSecondary'>
            Loading Stripe products...
          </Typography>
        </Box>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid item xs={12}>
        <Box p={2} style={{ backgroundColor: '#ffebee', borderRadius: '4px', border: '1px solid #f44336' }}>
          <Typography variant='body2' style={{ color: '#d32f2f' }}>
            {error}
          </Typography>
        </Box>
      </Grid>
    );
  }

  if (!products || products.length === 0) {
    return (
      <Grid item xs={12}>
        <Box p={2} style={{ backgroundColor: '#fff3e0', borderRadius: '4px', border: '1px solid #ff9800' }}>
          <Typography variant='body2' style={{ color: '#e65100' }}>
            No active products found in your Stripe account. Please create products in your Stripe Dashboard first.
          </Typography>
        </Box>
      </Grid>
    );
  }

  const productSelectItems = products.map((product) => ({
    label: formatProductDisplay(product),
    value: product.id,
    product: product, // Store the full product data
  }));

  return (
    <>
      <Grid item xs={12}>
        <Select
          fullWidth
          slow
          label='Stripe Product'
          name='stripeProductId'
          variant='outlined'
          required
          placeholder='Select a product from your Stripe account'
          items={productSelectItems}
          renderFunction={(item) => item.label}
          valueFunction={(item) => item.value}
        />
      </Grid>
    </>
  );
};
